import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Color,
  ColorPreset,
  Field,
  FormFieldStatus,
  Hint,
  JustifyContent,
  Label,
  PasswordInput,
  Text,
  XYGrid,
} from "@gocardless/flux-react";
import { useLingui } from "@lingui/react";
import { Trans, t } from "@lingui/macro";

import { SignupFormData } from "../../consts";
import { TrackingErrorOrigin } from "../../../consts";

import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import {
  getInputErrorStatus,
  passwordLengthValidation,
} from "src/utils/inputValidation";

const followsPasswordUserInfoPolicy = (passwordValue: string, email: string) =>
  !email || !passwordValue.includes(email.toLowerCase());

export const PasswordField: React.FC = () => {
  const { i18n } = useLingui();
  const { sendEvent } = useSegment();
  const {
    register,
    formState: { errors },
  } = useFormContext<SignupFormData>();

  const [passwordLength, setPasswordLength] = useState<number>(0);

  const watchedEmail = useWatch({ name: "email" });
  const minLength = passwordLengthValidation(watchedEmail);
  const maxLength = 72;

  const ErrorMessage = {
    maxLength: i18n._(
      t({
        id: "optimised-signup.user.password.password-too-long",
        message: "Password must be between 12 to 72 characters",
      })
    ),
    passwordUserInfoPolicy: i18n._(
      t({
        id: "optimised-signup.user.password.password-policy-failed",
        message: "Your password cannot contain your email address",
      })
    ),
    required: i18n._(
      t({
        id: "optimised-signup.user.password.field-required-warning",
        message: "Password is required",
      })
    ),
    minLength: i18n._(
      t({
        id: "optimised-signup.user.password.password-too-short",
        message: `Password must be at least ${minLength} characters`,
      })
    ),
  };

  const ButtonLabelText = {
    show: i18n._(
      t({
        id: "optimised-signup.password-input.accessory-label.show",
        message: "Show password",
      })
    ),
    hide: i18n._(
      t({
        id: "optimised-signup.password-input.accessory-label.hide",
        message: "Hide password",
      })
    ),
  };

  const getColor = (length: number) => {
    if ((0 < length && length < minLength) || length > maxLength) {
      return Color.Alert_Base;
    }
    if (length >= minLength) {
      return Color.Success_Base;
    }

    return ColorPreset.TextOnLight_01;
  };

  const getSegmentEventLabel = (value: string) => {
    if (!value) {
      return ErrorMessage.required;
    }
    const lowercaseValue = value.toLowerCase();

    if (passwordLength < minLength) {
      return ErrorMessage.minLength;
    }
    if (passwordLength > maxLength) {
      return ErrorMessage.maxLength;
    }
    if (!followsPasswordUserInfoPolicy(lowercaseValue, watchedEmail)) {
      return ErrorMessage.passwordUserInfoPolicy;
    }

    return "";
  };

  return (
    <Field data-testid="password">
      <XYGrid autoFlow="column" justifyContent={JustifyContent.SpaceBetween}>
        <Label htmlFor="password">
          <Trans id="optimised-sign-up.form.password">Password</Trans>
        </Label>
        <Text size={2} weight={500} color={getColor(passwordLength)}>
          {passwordLength}/{minLength}
        </Text>
      </XYGrid>
      <PasswordInput
        id="password"
        status={getInputErrorStatus(!!errors.password)}
        showButtonLabel={ButtonLabelText.show}
        hideButtonLabel={ButtonLabelText.hide}
        {...register("password", {
          required: ErrorMessage.required,
          minLength: {
            value: minLength,
            message: i18n._(
              t({
                id: "optimised-signup.user.password.password-too-short",
                message: `Password must be at least ${minLength} characters`,
              })
            ),
          },
          maxLength: {
            value: maxLength,
            message: ErrorMessage.maxLength,
          },
          validate: (value) =>
            followsPasswordUserInfoPolicy(value.toLowerCase(), watchedEmail) ||
            ErrorMessage.passwordUserInfoPolicy,
          onChange: (e) => setPasswordLength(e.currentTarget.value.length),
          onBlur: (e) => {
            const value = e.currentTarget.value;
            sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
              label: "Password",
              type: "Blur",
            });

            if (
              !value ||
              errors.password?.message ||
              passwordLength < minLength ||
              passwordLength > maxLength
            ) {
              sendEvent(TrackingEvent.SIGNUP_PASSWORD_FIELD_VALIDATION_FAILED, {
                label: getSegmentEventLabel(value),
                error_origin: TrackingErrorOrigin.client_side,
              });
            }
          },
        })}
      />
      {errors?.password && (
        <Hint status={FormFieldStatus.Danger}>{errors.password.message}</Hint>
      )}
    </Field>
  );
};
